'use client';

import { useAuth } from '@clerk/clerk-react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { apiClient } from '@/lib/api/client';
import type { components } from '@/lib/api/generated/schema';

type OnboardingStatusResponse =
  components['schemas']['OnboardingStatusResponse'];

export function useOnboardingStatus() {
  const {
    isSignedIn,
    isLoaded,
    getToken,
    orgId: clerkOrganizationId,
  } = useAuth();

  return useQuery<OnboardingStatusResponse>({
    // Key by clerkOrganizationId so a first fetch made with a stale, organization-less Clerk token (the active organization
    // lands a beat after sign-in) self-corrects the instant the organization arrives: the key changes,
    // react-query refetches with the now organization-scoped token, and an already-onboarded user
    // resolves 'active' (instead of a non-active result sticking forever under retry:false).
    queryKey: ['onboarding', 'status', clerkOrganizationId],
    queryFn: async () => {
      const token = await getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const { data, error } = await apiClient.GET('/api/v1/onboarding/status', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (error) {
        throw new Error('Failed to fetch onboarding status');
      }

      return data as OnboardingStatusResponse;
    },
    // Only fetch when user is authenticated - prevents 401 redirect for unauthenticated users
    enabled: isLoaded && isSignedIn === true,
    // Don't retry on failure - new users won't have an organization yet and that's expected
    retry: false,
    // When clerkOrganizationId lands and the key changes, keep the prior status rendered while the new
    // organization-keyed fetch runs, instead of dropping to isLoading (which would flash the skeleton
    // over the onboarding form mid-session). The refetch still resolves under the new organization,
    // so an already-onboarded user still flips to 'active' and redirects.
    placeholderData: keepPreviousData,
  });
}
